import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { X, CheckCircle2, Phone, MessageCircle, Mail, CalendarDays, MapPin, Users, Route } from "lucide-react";
import { BUSINESS, telLink, waLink, mailLink } from "@/data/business";

const EnquiryCtx = createContext(null);
export const useEnquiry = () => useContext(EnquiryCtx);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PICKUPS = ["Dehradun", "Haridwar", "Rishikesh", "Delhi", "Mussoorie", "Nainital", "Other city"];
const DAYS = ["1 day", "2 days", "3 days", "5 days", "7 days", "Customised trip"];

export function EnquiryProvider({ children }) {
  const [state, setState] = useState(null); // {destination, source, tripDays}

  const openEnquiry = useCallback((destination, source, opts = {}) => {
    setState({ destination: destination || "", source: source || "", ...opts });
  }, []);
  const close = useCallback(() => setState(null), []);

  return (
    <EnquiryCtx.Provider value={{ openEnquiry }}>
      {children}
      {state && <EnquiryModal {...state} onClose={close} />}
    </EnquiryCtx.Provider>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-snow placeholder:text-slategray outline-none transition-colors focus:border-gold/60 focus:bg-white/10";
const labelCls = "mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-mist";

function EnquiryModal({ destination, source, tripDays, onClose }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", pickup_city: "Dehradun", destination: destination || "",
    travel_date: "", passengers: "2", trip_type: "Round trip", days: tripDays || "2 days", stops: "", requirements: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const de = document.documentElement.style;
    const b = document.body.style;
    de.overflow = "hidden";
    b.overflow = "hidden";
    return () => {
      de.overflow = "";
      b.overflow = "";
    };
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.destination.trim() || !form.pickup_city.trim()) {
      setError("Please fill in your name, phone, pickup city and destination.");
      return;
    }
    setSaving(true);
    try {
      await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: source || "website" }),
      });
      setDone(true);
    } catch (err) {
      setError("Could not save the enquiry right now — please WhatsApp or call us directly, we're happy to help.");
    } finally {
      setSaving(false);
    }
  };

  const waMsg = `Hello Tingadya Travels! I'd like to enquire about a taxi trip.%0A%0A📍 Destination: ${form.destination}%0A🚖 Pickup: ${form.pickup_city}%0A📅 Date: ${form.travel_date || "flexible"}%0A👥 Passengers: ${form.passengers}%0A🔁 Trip: ${form.trip_type} (${form.days})${form.stops ? `%0A➕ Stops: ${form.stops}` : ""}${form.requirements ? `%0A📝 Notes: ${form.requirements}` : ""}`;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center" onClick={onClose} data-testid="enquiry-modal-overlay">
      <div
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-gold/25 bg-coal p-6 shadow-2xl sm:rounded-3xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="enquiry-modal"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Travel with Tingadya Travels</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-white">
              {done ? "Enquiry received" : "Plan your taxi trip"}
            </h2>
            {form.destination && !done && (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-gold"><MapPin size={14} /> {form.destination}</p>
            )}
          </div>
          <button onClick={onClose} data-testid="enquiry-modal-close-button" aria-label="Close enquiry form" className="rounded-full border border-white/10 p-2 text-mist transition-colors hover:text-white">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div data-testid="enquiry-success-panel">
            <div className="flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-4">
              <CheckCircle2 className="mt-0.5 shrink-0 text-gold" size={22} />
              <p className="text-sm leading-relaxed text-snow">
                Thank you, {form.name.split(" ")[0]} — your enquiry for <span className="font-semibold text-gold">{form.destination}</span> is saved. Our team usually responds quickly during working hours. Reach us instantly on any channel below:
              </p>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <a href={telLink(BUSINESS.phoneIndiaRaw)} data-testid="success-call-india-button" className="btn-gold w-full">
                <Phone size={16} /> Call {BUSINESS.phoneIndia}
              </a>
              <a href={`https://wa.me/${BUSINESS.phoneIndiaRaw.slice(1)}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" data-testid="success-whatsapp-india-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-ink transition-transform hover:scale-[1.02]">
                <MessageCircle size={16} /> WhatsApp {BUSINESS.phoneIndia}
              </a>
              <a href={telLink(BUSINESS.phoneIntlRaw)} data-testid="success-call-intl-button" className="btn-ghost-gold w-full">
                <Phone size={16} /> Call {BUSINESS.phoneIntl}
              </a>
              <a href={`https://wa.me/${BUSINESS.phoneIntlRaw.slice(1).replace(/\D/g, "")}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" data-testid="success-whatsapp-intl-button" className="btn-ghost-gold w-full">
                <MessageCircle size={16} /> WhatsApp {BUSINESS.phoneIntl}
              </a>
              <a href={mailLink(`Taxi enquiry — ${form.destination}`, `Hello Tingadya Travels,%0A%0AI submitted an enquiry for ${form.destination}.%0A%0APickup: ${form.pickup_city}%0ADate: ${form.travel_date || "flexible"}%0APassengers: ${form.passengers}`)} data-testid="success-email-button" className="btn-white w-full sm:col-span-2">
                <Mail size={16} /> Email {BUSINESS.email}
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="enquiry-form">
            <div>
              <label className={labelCls} htmlFor="eq-name">Your name *</label>
              <input id="eq-name" required data-testid="enquiry-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-phone">Phone / WhatsApp *</label>
              <input id="eq-phone" required data-testid="enquiry-phone-input" className={inputCls} placeholder="Phone number" inputMode="tel" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-email">Email (optional)</label>
              <input id="eq-email" type="email" data-testid="enquiry-email-input" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-pickup"><MapPin size={12} /> Pickup city *</label>
              <select id="eq-pickup" data-testid="enquiry-pickup-select" className={inputCls} value={form.pickup_city} onChange={set("pickup_city")}>
                {PICKUPS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-dest">Destination *</label>
              <input id="eq-dest" required data-testid="enquiry-destination-input" className={inputCls} placeholder="e.g. Kedarnath" value={form.destination} onChange={set("destination")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-date"><CalendarDays size={12} /> Travel date</label>
              <input id="eq-date" type="date" data-testid="enquiry-date-input" className={inputCls} value={form.travel_date} onChange={set("travel_date")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-passengers"><Users size={12} /> Passengers</label>
              <select id="eq-passengers" data-testid="enquiry-passengers-select" className={inputCls} value={form.passengers} onChange={set("passengers")}>
                {["1", "2", "3", "4", "5", "6", "7"].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-triptype"><Route size={12} /> Trip type</label>
              <select id="eq-triptype" data-testid="enquiry-triptype-select" className={inputCls} value={form.trip_type} onChange={set("trip_type")}>
                <option>One-way</option>
                <option>Round trip</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="eq-days">Number of days</label>
              <select id="eq-days" data-testid="enquiry-days-select" className={inputCls} value={form.days} onChange={set("days")}>
                {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="eq-stops">Additional stops</label>
              <input id="eq-stops" data-testid="enquiry-stops-input" className={inputCls} placeholder="Places you'd like to add on the way" value={form.stops} onChange={set("stops")} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="eq-req">Special requirements</label>
              <textarea id="eq-req" rows={3} data-testid="enquiry-requirements-input" className={inputCls} placeholder="Senior citizens, kids, luggage, tempo needs…" value={form.requirements} onChange={set("requirements")} />
            </div>
            {error && <p className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300 sm:col-span-2" data-testid="enquiry-error-message">{error}</p>}
            <button type="submit" disabled={saving} data-testid="enquiry-submit-button" className="btn-gold w-full disabled:opacity-60 sm:col-span-2">
              {saving ? "Sending…" : "Send Enquiry — Save My Request"}
            </button>
            <p className="text-center text-xs text-slategray sm:col-span-2">
              Prefer instant chat?{" "}
              <a href={`https://wa.me/${BUSINESS.phoneIndiaRaw.slice(1)}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" data-testid="enquiry-instant-whatsapp-link" className="font-semibold text-[#25D366] hover:underline">
                WhatsApp us now with these details
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
